import { getAccessToken, getAuthMode, setAuthMode, setAuthSessionItem } from '@/lib/auth-storage'

export interface ParkingOsSession {
  parkingId: string
  token: string
  parkName: string
}

const SESSION_KEY = 'parkingOsSession'

export function getParkingOsSession(): ParkingOsSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ParkingOsSession
    if (!parsed.parkingId || !parsed.token) return null
    return parsed
  } catch {
    return null
  }
}

export function setParkingOsSession(session: ParkingOsSession): void {
  if (typeof window === 'undefined') return
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

export function clearParkingOsSession(): void {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(SESSION_KEY)
}

export function parseParkingOsSessionFromParams(params: URLSearchParams): ParkingOsSession | null {
  const parkingId = params.get('b')?.trim()
  const token = params.get('a')?.trim()
  const parkName = params.get('parkName')?.trim() || ''
  if (!parkingId || !token) return null
  const session = { parkingId, token, parkName }
  setParkingOsSession(session)
  const accessToken = getAccessToken()?.trim() || ''
  if (!accessToken || accessToken === token) {
    setAuthSessionItem('accessToken', token)
  }
  if (!getAuthMode()) setAuthMode('platform')
  return session
}

export function appendParkingOsSessionToHref(href: string, session: ParkingOsSession): string {
  const [path, query = ''] = href.split('?')
  const params = new URLSearchParams(query)
  params.set('b', session.parkingId)
  params.set('a', session.token)
  if (session.parkName) params.set('parkName', session.parkName)
  return `${path}?${params.toString()}`
}

export function getEffectiveAccessToken(): string | null {
  return getAccessToken()?.trim() || getParkingOsSession()?.token?.trim() || null
}

export function ensureParkingOsSessionToken(session: ParkingOsSession): ParkingOsSession {
  const token = getEffectiveAccessToken()?.trim() || session.token?.trim() || ''
  return { ...session, token }
}

export function isSessionKickedMessage(message: unknown): boolean {
  if (typeof message !== 'string' || !message) return false
  return /另一地点登录|其他设备登录|在其他设备登录|会话已下线|被迫下线/.test(message)
}

export function extractAuthErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: { msg?: string } } }).response?.data
    if (data?.msg) return data.msg
  }
  return ''
}

export function isAuthHttpError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { status?: number; data?: { code?: unknown } } }).response
    if (response?.status === 401 || response?.status === 403) return true
    const code = response?.data?.code
    return code === 20007 || code === 20009 || code === 4001 || code === 4006
  }
  return false
}

export async function withParkingOsAuthRetry<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch (error) {
    if (!isAuthHttpError(error) || isSessionKickedMessage(extractAuthErrorMessage(error))) {
      throw error
    }
    const sessionToken = getParkingOsSession()?.token?.trim()
    if (sessionToken) {
      setAuthSessionItem('accessToken', sessionToken)
      return await fn()
    }
    throw error
  }
}

const BOOTH_DUTY_SESSION_PREFIX = 'boothDutySession:'
const BOOTH_DUTY_COLLECTOR_PREFIX = 'boothDutyCollector:'
const BOOTH_DUTY_LOGGED_IN_PREFIX = 'boothDutyLoggedIn:'
const BOOTH_DUTY_FORCE_CLOCKOUT_PREFIX = 'boothForceClockOut:'

/** 记录本窗口是否收到过「强制下班」踢出，进入云岗亭时优先弹窗提示 */
export function saveBoothForceClockOutFlag(parkingId: string): void {
  if (!parkingId) return
  sessionStorage.setItem(`${BOOTH_DUTY_FORCE_CLOCKOUT_PREFIX}${parkingId}`, '1')
}

export function loadBoothForceClockOutFlag(parkingId: string): boolean {
  if (!parkingId) return false
  return sessionStorage.getItem(`${BOOTH_DUTY_FORCE_CLOCKOUT_PREFIX}${parkingId}`) === '1'
}

export function clearBoothForceClockOutFlag(parkingId: string): void {
  if (!parkingId) return
  sessionStorage.removeItem(`${BOOTH_DUTY_FORCE_CLOCKOUT_PREFIX}${parkingId}`)
}

export function loadBoothDutySessionId(parkingId: string): string {
  if (!parkingId) return ''
  return sessionStorage.getItem(`${BOOTH_DUTY_SESSION_PREFIX}${parkingId}`)?.trim() ?? ''
}

export function saveBoothDutySessionId(parkingId: string, sessionId: string): void {
  if (!parkingId || !sessionId) return
  sessionStorage.setItem(`${BOOTH_DUTY_SESSION_PREFIX}${parkingId}`, sessionId)
}

export function clearBoothDutySessionId(parkingId: string): void {
  if (!parkingId) return
  sessionStorage.removeItem(`${BOOTH_DUTY_SESSION_PREFIX}${parkingId}`)
}

export function saveBoothDutyCollectorName(parkingId: string, collectorName: string): void {
  if (!parkingId || !collectorName.trim()) return
  sessionStorage.setItem(`${BOOTH_DUTY_COLLECTOR_PREFIX}${parkingId}`, collectorName.trim())
}

export function loadBoothDutyCollectorName(parkingId: string): string {
  if (!parkingId) return ''
  return sessionStorage.getItem(`${BOOTH_DUTY_COLLECTOR_PREFIX}${parkingId}`)?.trim() ?? ''
}

export function clearBoothDutyCollectorName(parkingId: string): void {
  if (!parkingId) return
  sessionStorage.removeItem(`${BOOTH_DUTY_COLLECTOR_PREFIX}${parkingId}`)
}

export function saveBoothDutyLoggedIn(parkingId: string): void {
  if (!parkingId) return
  sessionStorage.setItem(`${BOOTH_DUTY_LOGGED_IN_PREFIX}${parkingId}`, '1')
}

export function isBoothDutyLoggedIn(parkingId: string): boolean {
  if (!parkingId) return false
  return sessionStorage.getItem(`${BOOTH_DUTY_LOGGED_IN_PREFIX}${parkingId}`) === '1'
}

export function clearBoothDutyLoggedIn(parkingId: string): void {
  if (!parkingId) return
  sessionStorage.removeItem(`${BOOTH_DUTY_LOGGED_IN_PREFIX}${parkingId}`)
}
